import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import ProjectCard from "../../components/Boxs/ProjectCard";
import TeammateBox from "../../components/Boxs/TeammateBox";
import Page from "../../components/Page";
import { inputTextSearchAtom } from "../../components/ProjectFilters/atom/ProjectFiltersAtom";
import ProjectFilters, {
  lookForAtom,
} from "../../components/ProjectFilters/ProjectFilters";
import { url } from "../../url";
import { userProjectsAtom } from "../Projects/atoms/UserProjectAtomGlobal";
import { teammatesAtom } from "../Teammate/atoms/TeammateAtomGlobal";
import { userTeammatesAtom } from "../Teammate/atoms/UserTeammatesAtomGlobal";
import {
  hasMoreProjectsAtom,
  indexAtom,
  projectsAtom,
} from "./atoms/ProjectAtomGlobal";
import styles from "./projects.module.scss";

const Projects = () => {
  const [showFilters, setShowFilters] = useState(true);
  const [showSkillers, setShowSkillers] = useState(false);
  const [projects, setProjects] = useAtom<any[]>(projectsAtom);
  const [userProjects, setUserProjects] = useAtom(userProjectsAtom);
  const [userCreatedProjectsIds, setUserCreatedProjectsIds] = useState("");
  const [projectsAdded, setProjectsAdded] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useAtom<boolean>(hasMoreProjectsAtom);
  const [index, setIndex] = useAtom(indexAtom);
  const [error, setError] = useState(false);
  const [infoMsg, setInfoMsg] = useState("");
  const [teammates, setTeammates] = useAtom<any[]>(teammatesAtom);
  const lookFor = useAtomValue(lookForAtom);
  const [userTeammates, setUserTeammates] = useAtom(userTeammatesAtom);
  const [userTeammateProfileId, setUserTeammateProfileId] = useState("");
  const [teammateAdded, setTeammateAdded] = useState<any>({});
  const setInputTextSearch = useSetAtom(inputTextSearchAtom);

  // Nowa, uproszczona logika filtrowania:
  // Centralna funkcja do wyświetlania JSONa (z index_new)
  const displayJson = (jsonData: any) => {
    console.log("=== DISPLAY JSON FUNCTION CALLED ===");
    console.log("Received JSON data:", jsonData);

    // Nowy format: items (projekty + teammateci)
    if (jsonData && Array.isArray(jsonData.items)) {
      setProjects(jsonData.items.filter((item: any) => item.projectName));
      setTeammates(jsonData.items.filter((item: any) => item.nickname));
      // Dodaj komunikat jeśli są similarity (AI fallback)
      if (
        jsonData.items.length > 0 &&
        jsonData.items.some((item: any) => item.similarity !== undefined)
      ) {
        setInfoMsg("Sorry we didn't find perfect match, checkout closest hits");
      } else {
        setInfoMsg("");
      }
    } else if (jsonData && jsonData.projects) {
      setProjects(jsonData.projects);
      setInfoMsg("");
    } else if (Array.isArray(jsonData)) {
      setProjects(jsonData);
      setInfoMsg("");
    } else {
      console.warn("Invalid JSON data received:", jsonData);
      setInfoMsg("");
    }
    setLoading(false);
    console.log("=== DISPLAY JSON FUNCTION COMPLETED ===");
  };

  useEffect(() => {
    // Obsługa search z głównej strony
    const searchText = localStorage.getItem("searchText");
    if (searchText) {
      setInputTextSearch(searchText);
      localStorage.removeItem("searchText");
      // Znajdź Adjust Filters button i kliknij go programowo po ustawieniu inputa
      setTimeout(() => {
        const btn = document.querySelector(".apply_button");
        if (btn) (btn as HTMLButtonElement).click();
      }, 100);
    }
    // Fetch all projects and teammates on start
    const fetchProjectsAndTeammates = async () => {
      try {
        const [projectsRes, teammatesRes] = await Promise.all([
          axios.get(url + "/project?page=1"),
          axios.get(url + "/teammate?page=1"),
        ]);
        // Projects
        displayJson(projectsRes.data);
        // Teammates
        setTeammates(teammatesRes.data.teammates || []);
        // Fetch user teammates info
        const token = localStorage.getItem("token");
        if (token) {
          const userTeammatesRes = await axios.get(
            url + "/user/teammates/found",
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const userTeammateArr = userTeammatesRes.data.teammates;
          setUserTeammates(userTeammateArr);
          (teammatesRes.data.teammates || []).forEach((teammate: any) => {
            if (userTeammateArr?.includes(teammate._id)) {
              setTeammateAdded((prevState: any) => ({
                ...prevState,
                [teammate._id]: true,
              }));
            } else {
              setTeammateAdded((prevState: any) => ({
                ...prevState,
                [teammate._id]: false,
              }));
            }
          });
          try {
            const TeammateProfile = await axios.get(url + "/user/myTeammate", {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            });
            if (TeammateProfile.data.userTeammateProfile?._id) {
              setUserTeammateProfileId(
                TeammateProfile.data.userTeammateProfile._id
              );
            }
          } catch (error) {
            // User doesn't have a teammate profile yet
            setUserTeammateProfileId("");
          }

          // Fetch user created projects IDs
          const userCreatedProjectsRes = await axios.get(
            url + "/user/myProjectsIds",
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setUserCreatedProjectsIds(userCreatedProjectsRes.data.myProjectsIds);
        }
      } catch (err) {
        console.error("Error fetching projects or teammates:", err);
        setLoading(false);
      }
    };
    fetchProjectsAndTeammates();
  }, [setProjects, setTeammates, setUserTeammates]);

  // Dodaję logikę filtrowania z index.tsx
  // 1. Dodaję useState infoMsg, showFilters, loading
  // 2. Funkcja displayJson
  const tokenAvailable = () => {
    const token = localStorage.getItem("token");
    if (token) {
      return true;
    }
    return false;
  };

  const addUserProject = async (project: any): Promise<void> => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        await axios.post(
          url + `/user/projects/${project._id}`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setUserProjects([...userProjects, project._id]);
      } catch (err) {
        console.log(err);
      }
    }
    setProjectsAdded((prevState: any) => ({
      ...prevState,
      [project._id]: true,
    }));
  };

  const deleteUserProject = async (project: any): Promise<void> => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        await axios.delete(url + `/user/projects/${project._id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        setUserProjects(
          userProjects.filter(function (p: any) {
            return p !== project._id;
          })
        );
      } catch (err) {
        console.log(err);
      }
    }
    setProjectsAdded((prevState: any) => ({
      ...prevState,
      [project._id]: false,
    }));
  };

  const addTeammate = async (teammate: any): Promise<void> => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        await axios.post(
          url + `/user/teammates/${teammate._id}`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setUserTeammates([...userTeammates, teammate._id]);
      } catch (err) {
        console.log(err);
      }
    }
    setTeammateAdded((prevState: any) => ({
      ...prevState,
      [teammate._id]: true,
    }));
  };

  const deleteUserTeammates = async (teammate: any): Promise<void> => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        await axios.delete(url + `/user/teammates/${teammate._id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        setUserTeammates(
          userTeammates.filter(function (t: any) {
            return t !== teammate._id;
          })
        );
      } catch (err) {
        console.log(err);
      }
    }
    setTeammateAdded((prevState: any) => ({
      ...prevState,
      [teammate._id]: false,
    }));
  };

  // Dodaję z powrotem funkcje potrzebne do działania UI:
  const handleShowFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const fetchMoreProjects = async () => {
    // Pobieranie kolejnych projektów do infinite scroll (zostawiam dotychczasową logikę)
    // Uwaga: query nie jest już używane, więc pobieramy kolejne strony bez filtrów
    try {
      const res = await axios.get(url + `/project/?page=${index}`);
      const projectsArr = res.data.projects;
      setProjects((prevProjects: any[]) => [...prevProjects, ...projectsArr]);
      projectsArr.length > 0 ? setHasMore(true) : setHasMore(false);
      setIndex((prevIndex) => prevIndex + 1);
    } catch (err) {
      console.error(err);
      setHasMore(false);
    }
  };
  if (error) {
    return <Navigate to="/error" />;
  }

  return (
    <Page style={{ position: "relative", overflow: "unset" }}>
      <section className="projects">
        <header className="container projects__header">
          <div className="projects__header__left"></div>
          <div className="projects__header__right">
            <div className="projects__header__right__skillerButton"></div>
          </div>
        </header>

        {/* Animowane/sticky filtry z przyciskiem toggle, który zostaje widoczny */}

        <div
          className={
            showFilters
              ? "filters-open all-filters"
              : "filters-close all-filters"
          }
        >
          <div className="filters-close-toggle" onClick={handleShowFilters}>
            <span className="filters-close-toggle__icon" />
          </div>
          {showFilters && (
            <div
              style={{
                opacity: showFilters ? 1 : 0,
                transform: showFilters ? "translateY(0)" : "translateY(-20px)",
                transition: "all 0.3s ease",
                pointerEvents: showFilters ? "auto" : "none",
                visibility: showFilters ? "visible" : "hidden",
              }}
              className={styles.filter_box}
            >
              <ProjectFilters
                onDisplayJson={displayJson}
                onCloseFilters={handleShowFilters}
              />
            </div>
          )}
        </div>

        {/* Komunikat o trybie i info */}
        {infoMsg && (
          <div
            style={{
              background: "#e3f2fd",
              border: "1px solid #90caf9",
              borderRadius: "10px",
              padding: "12px 18px",
              margin: "18px 0 10px 0",
              textAlign: "center",
              color: "#1565c0",
              fontSize: "15px",
              fontWeight: 500,
              maxWidth: 600,
              marginLeft: "auto",
              marginRight: "auto",
              boxShadow: "0 2px 8px 0 rgba(21,101,192,0.07)",
            }}
          >
            {infoMsg}
          </div>
        )}

        {/* Scrollowalny kontener z wynikami */}
        <div
          className="projects__body"
          style={{
            transition: "all 0.3s ease",
          }}
        >
          <div className="projects__body__filterTabs">
            {/* {showFilters &&
            <ProjectFilterTabs index={0} />
          } */}
          </div>

          <div className="projects__body__container">
            <div className="projects__body__container__left">
              {loading && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    padding: "20px",
                  }}
                >
                  <CircularProgress style={{ color: "#3380FF" }} />
                </div>
              )}
              {/* Jeśli nie ma projektów i teammateów i nie trwa ładowanie, pokaż komunikat */}
              {!loading && projects.length + teammates.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    color: "#FF5733",
                    margin: "20px",
                    fontSize: "1.2rem",
                  }}
                >
                  Brak wyszukań
                </div>
              )}
              {/* Renderuj projekty i teammateów razem, z filtrem lookFor */}
              {(projects.length > 0 || teammates.length > 0) && (
                <div className={styles.projectsGrid}>
                  {[...projects, ...teammates]
                    .filter((item: any) => {
                      if (lookFor === "projects") return !!item.projectName;
                      if (lookFor === "teammates") return !!item.nickname;
                      return true;
                    })
                    .sort((a: any, b: any) => {
                      if (lookFor === "all") {
                        const dateA = new Date(
                          a.createdAt || a.created_at || 0
                        ).getTime();
                        const dateB = new Date(
                          b.createdAt || b.created_at || 0
                        ).getTime();
                        return dateB - dateA;
                      }
                      return 0;
                    })
                    .map((item: any) => {
                      if (item.projectName) {
                        // To projekt
                        const isMyProject = userCreatedProjectsIds?.includes(
                          item._id
                        );
                        const props = {
                          project: item,
                          loading: loading,
                          tokenAvailable: !isMyProject && tokenAvailable(),
                          addProject: !isMyProject
                            ? () => addUserProject(item)
                            : undefined,
                          deleteProject: !isMyProject
                            ? () => deleteUserProject(item)
                            : undefined,
                          projectAdded: !isMyProject && projectsAdded[item._id],
                          isMyProject: isMyProject,
                        };
                        return (
                          <div key={item._id} className={styles.projectCard}>
                            <ProjectCard {...props} />
                          </div>
                        );
                      } else if (item.nickname) {
                        // To teammate
                        const isMyTeammate =
                          userTeammateProfileId === item._id ||
                          item.myTeammate === true ||
                          item.isMyTeammate === true;

                        const props = {
                          teammate: item,
                          loading: loading,
                          tokenAvailable: !isMyTeammate && tokenAvailable(),
                          addTeammate: !isMyTeammate
                            ? () => addTeammate(item)
                            : undefined,
                          deleteTeammate: !isMyTeammate
                            ? () => deleteUserTeammates(item)
                            : undefined,
                          teammateAdded:
                            !isMyTeammate && teammateAdded[item._id],
                          isMyTeammate: isMyTeammate,
                        };
                        return (
                          <div key={item._id} className={styles.projectCard}>
                            <TeammateBox {...props} />
                          </div>
                        );
                      }
                      return null;
                    })}
                </div>
              )}
            </div>
            <div className="projects__body__container__right">
              {/* <AddedSkillers /> */}
            </div>
          </div>
        </div>
      </section>
    </Page>
  );
};

export default Projects;
